/**
 * UsageStat: a labelled meter, "8,412 / 25,000" with the percentage. Used on Usage and Billing.
 *
 *   <UsageStat label="Credits" used={8412} total={25000} />
 *   <UsageStat label="Proxy bandwidth" used={812} total={2000} formatValue={formatProxyMb} />
 *
 * The bar turns amber at 80% and red at 100%, the same thresholds as the billing summary.
 */
import { formatNumber } from "@/lib/format";

interface UsageStatProps {
  label: string;
  used: number;
  total: number;
  formatValue?: (value: number) => string;
}

export const WARN_AT = 80;

const UsageStat = ({ label, used, total, formatValue }: UsageStatProps) => {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const shown = Math.round(pct);
  const fmt = formatValue ?? ((v: number) => formatNumber(v));
  const bar = pct >= 100 ? "bg-danger" : pct >= WARN_AT ? "bg-warning" : "bg-primary";

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span className="text-13 tabular-nums text-fg-muted">{shown}%</span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={shown}
        aria-valuetext={`${fmt(used)} of ${fmt(total)} used`}
        className="h-2 w-full overflow-hidden rounded-full bg-surface-muted ring-1 ring-inset ring-line"
      >
        <div className={`h-2 rounded-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-13 tabular-nums text-fg-secondary">
        {fmt(used)} <span className="text-fg-muted">of {fmt(total)} used</span>
      </p>
    </div>
  );
};

export default UsageStat;
